import { Box, Menu, MenuItem, ListItemIcon, ListItemText } from '@mui/material'
import { Download, Place } from '@mui/icons-material'
import { useState } from 'react'
import { ConvertTime } from '@/components/smartUtils/conversion/ConvertTime'
import { preview } from '@/components/form/preview'
import { useEnvironmentURLs } from '@/atoms'
import { togglePoDMarker, fitMap, useActivePodMarker } from '@/map'
import { FilePreview } from '@workwave-tidal/lab'
import { ActionBarItem } from '@workwave-tidal/core/components/ActionBar'
import { MoreVert } from '@workwave-tidal/core/icons'

import { useTexts } from '../useTexts'

interface Props {
  extOrder: uui.domain.client.rm.ExtendedOrderStep
}

export function Signature(props: Props) {
  const { extOrder } = props
  const texts = useTexts()
  const { podsBaseUrl } = useEnvironmentURLs()
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const open = Boolean(anchorEl)
  const activePodMarker = useActivePodMarker()

  const signatures = extOrder.orderStep.trackingData?.pods?.signatures

  if (!signatures) {
    return (
      <preview.Field label={texts.signature.label} testId="execution-signature">
        <preview.NullValue />
      </preview.Field>
    )
  }

  const { customer } = signatures
  const { sec, text, uuid, latLng } = customer
  const time = <ConvertTime seconds={sec} showSeconds={false} />
  const src = customer.podType === 'signatures' ? `${podsBaseUrl}${customer.token}` : ''
  const isOnMap = !!uuid && activePodMarker?.uid === uuid

  const handleMenuClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget)
  }

  const handleMenuClose = () => {
    setAnchorEl(null)
  }

  const handleShowOnMap = () => {
    if (latLng) {
      togglePoDMarker(uuid, latLng)

      // Only fit when revealing the marker; toggling it off shouldn't recenter the map.
      if (!isOnMap) {
        fitMap([latLng], { minResolution: 3, preventIfVisible: true }, 'clear')
      }
    }
    handleMenuClose()
  }

  const handleDownload = () => {
    if (!src) return

    const link = document.createElement('a')
    link.href = src
    link.target = '_blank'
    link.rel = 'noopener noreferrer'
    link.download = 'customer_signature.png'
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    handleMenuClose()
  }

  return (
    <Box data-testid="execution-signature" sx={{ width: '100%' }}>
      <preview.Label>{texts.signature.label}</preview.Label>
      <Box sx={{ mb: 2, width: '100%' }}>
        <Box
          sx={{
            width: '100%',
            maxWidth: '100%',
            '& > *': {
              width: '100% !important',
              maxWidth: '100% !important',
            },
          }}
        >
          <FilePreview
            variant="image"
            size="large"
            src={src}
            alt={texts.customerSignature.label}
            tooltip={texts.customerSignature.label}
            objectFit="contain"
            actions={[
              <ActionBarItem
                key="menu"
                icon={<MoreVert />}
                onClick={handleMenuClick}
                label=""
                testId="signature-menu"
              />,
            ]}
          />
        </Box>
        <Menu
          anchorEl={anchorEl}
          open={open}
          onClose={handleMenuClose}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'right',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'right',
          }}
        >
          <MenuItem onClick={handleShowOnMap} disabled={!latLng}>
            <ListItemIcon>
              <Place fontSize="small" />
            </ListItemIcon>
            <ListItemText>{isOnMap ? texts.hideOnMap.label : texts.showOnMap.label}</ListItemText>
          </MenuItem>
          <MenuItem onClick={handleDownload}>
            <ListItemIcon>
              <Download fontSize="small" />
            </ListItemIcon>
            <ListItemText>{texts.downLoadImage.label}</ListItemText>
          </MenuItem>
        </Menu>
        <Box
          sx={{
            mt: 1,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            color: 'text.secondary',
            fontSize: '0.875rem',
          }}
        >
          <Box>
            {texts.customerSignature.label} • {time}
          </Box>
          {text && <Box sx={{ fontSize: '0.875rem', color: 'text.secondary' }}>{text}</Box>}
        </Box>
      </Box>
    </Box>
  )
}
